import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { catalogApiService, PublicCatalogResponse } from '@/services/catalogApi.service';

const CATALOG_TOKEN_KEY = 'catalog_access_token';
const CATALOG_EVENT_ID_KEY = 'catalog_event_id';

interface UseCatalogAuthReturn {
  isLoading: boolean;
  isValid: boolean | null;
  catalogData: PublicCatalogResponse | null;
  eventId: string | null;
}

export function useCatalogTokenRedirect(accessToken: string): UseCatalogAuthReturn {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [isValid, setIsValid] = useState<boolean | null>(null);
  const [catalogData, setCatalogData] = useState<PublicCatalogResponse | null>(null);
  const [eventId, setEventId] = useState<string | null>(null);

  useEffect(() => {
    async function validateAndStore() {
      try {
        const data = await catalogApiService.getPublicCatalog(accessToken);
        if (data) {
          localStorage.setItem(CATALOG_TOKEN_KEY, accessToken);
          localStorage.setItem(CATALOG_EVENT_ID_KEY, data.eventId);
          setCatalogData(data);
          setEventId(data.eventId);
          setIsValid(true);
          navigate('/vitrine', { replace: true });
        } else {
          setIsValid(false);
        }
      } catch {
        setIsValid(false);
      } finally {
        setIsLoading(false);
      }
    }
    validateAndStore();
  }, [accessToken]);

  return { isLoading, isValid, catalogData, eventId };
}

export function useCatalogFromStorage(): UseCatalogAuthReturn {
  const [isLoading, setIsLoading] = useState(true);
  const [isValid, setIsValid] = useState<boolean | null>(null);
  const [catalogData, setCatalogData] = useState<PublicCatalogResponse | null>(null);
  const [eventId, setEventId] = useState<string | null>(null);

  useEffect(() => {
    async function validateFromStorage() {
      const storedToken = localStorage.getItem(CATALOG_TOKEN_KEY);
      if (!storedToken) {
        setIsValid(false);
        setIsLoading(false);
        return;
      }
      try {
        const data = await catalogApiService.getPublicCatalog(storedToken);
        if (data) {
          setEventId(localStorage.getItem(CATALOG_EVENT_ID_KEY));
          setCatalogData(data);
          setIsValid(true);
        } else {
          localStorage.removeItem(CATALOG_TOKEN_KEY);
          localStorage.removeItem(CATALOG_EVENT_ID_KEY);
          setIsValid(false);
        }
      } catch {
        setIsValid(false);
      } finally {
        setIsLoading(false);
      }
    }
    validateFromStorage();
  }, []);

  return { isLoading, isValid, catalogData, eventId };
}
